import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * CreditHistoryDrawer — a slide-style modal that fetches the authed user's
 * own ledger via `/credits/me` on open and renders a small table.
 *
 * Used by both `CustomerDashboard` and `ProviderDashboard` (same shape; the
 * endpoint auto-detects role server-side).
 *
 * Props:
 *   open: boolean
 *   onOpenChange(next: boolean): void
 *   creditLabel: string         — display label from ConfigContext ("PooBucks")
 *   currentBalance: number      — already-known balance from the parent so we
 *                                 can show it in the modal header even before
 *                                 the ledger fetch completes
 */
export default function CreditHistoryDrawer({ open, onOpenChange, creditLabel, currentBalance }) {
  const [loading, setLoading] = useState(false);
  const [ledger, setLedger] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/credits/me")
      .then(({ data }) => {
        if (cancelled) return;
        setLedger(data.ledger || []);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto rounded-3xl bg-pp-surface"
        data-testid="credit-history-drawer"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-pp-text">
            {creditLabel} history
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Current balance
          </p>
          <p
            className="mt-1 font-heading text-4xl font-bold text-brand-orange"
            data-testid="credit-history-balance"
          >
            ${Number(currentBalance || 0).toFixed(2)}
          </p>
        </div>

        <div className="mt-5">
          {loading ? (
            <p className="flex items-center gap-2 text-sm text-pp-text-muted" data-testid="credit-history-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          ) : ledger.length === 0 ? (
            <p
              className="rounded-2xl border border-dashed border-pp-border p-6 text-center text-sm text-pp-text-muted"
              data-testid="credit-history-empty"
            >
              No {creditLabel} activity yet.
            </p>
          ) : (
            <ul className="divide-y divide-pp-border" data-testid="credit-history-list">
              {ledger.map((e) => {
                const positive = e.amount >= 0;
                return (
                  <li
                    key={e.id}
                    className="flex items-start justify-between gap-3 py-3"
                    data-testid={`credit-history-row-${e.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-pp-text">{e.reason}</p>
                      <p className="mt-1 text-xs text-pp-text-muted">
                        {e.created_at?.slice(0, 16).replace("T", " ")} · by {e.admin_email}
                      </p>
                    </div>
                    <p
                      className={
                        "shrink-0 font-heading text-lg font-bold " +
                        (positive ? "text-brand-orange" : "text-slate-500 dark:text-slate-400")
                      }
                    >
                      {positive ? "+" : "−"}${Math.abs(e.amount).toFixed(2)}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
